import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { WORKSPACE_MATTER, WORKSPACE_PARTIES, WORKSPACE_KEY_DATES, WORKSPACE_DOCUMENTS, WORKSPACE_CHRONOLOGY, WORKSPACE_SEARCH_HISTORY, WORKSPACE_REVIEW_ITEMS, WORKSPACE_AUDIT_LOG, WORKSPACE_WORKFLOWS, WORKSPACE_ACTIVITY, DOC_CATEGORY_COUNTS, SOURCE_COUNTS, MATTER_TAGS } from '../lib/workspace-data';
import { ArrowLeft, Upload, BrainCircuit, FileText, Clock, GitBranch, PenTool, CheckSquare, Shield, Search, Filter, Download, MoreVertical, AlertTriangle, CheckCircle2, XCircle, ChevronRight, FileSearch, Users, Calendar, Tag, Database, Activity, Play, Settings2, RefreshCw } from 'lucide-react';
interface MatterWorkspaceProps {
  onNavigate: (page: string) => void;
}
type TabType = 'overview' | 'documents' | 'search' | 'chronology' | 'workflows' | 'drafting' | 'review' | 'audit';
export function MatterWorkspace({
  onNavigate
}: MatterWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    triggerSearch();
  };
  const triggerSearch = () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setShowResults(false);
    setTimeout(() => {
      setIsSearching(false);
      setShowResults(true);
    }, 1500);
  };
  const renderTabNavigation = () => {
    const tabs: {
      id: TabType;
      label: string;
      icon: React.ElementType;
      count?: number;
    }[] = [{
      id: 'overview',
      label: 'Overview',
      icon: Activity
    }, {
      id: 'documents',
      label: 'Documents',
      icon: FileText,
      count: WORKSPACE_DOCUMENTS.length
    }, {
      id: 'search',
      label: 'Search & Answers',
      icon: Search
    }, {
      id: 'chronology',
      label: 'Chronology',
      icon: Clock,
      count: WORKSPACE_CHRONOLOGY.length
    }, {
      id: 'workflows',
      label: 'Workflows',
      icon: GitBranch,
      count: WORKSPACE_WORKFLOWS.filter(w => w.status === 'Running').length || undefined
    }, {
      id: 'drafting',
      label: 'Drafting',
      icon: PenTool
    }, {
      id: 'review',
      label: 'Review Queue',
      icon: CheckSquare,
      count: WORKSPACE_REVIEW_ITEMS.filter(r => r.status === 'Pending').length
    }, {
      id: 'audit',
      label: 'Audit Trail',
      icon: Shield
    }];
    return <div className="flex space-x-1 border-b overflow-x-auto pb-px mb-6 hide-scrollbar" data-id="element-1446">
        {tabs.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${isActive ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`} data-id="element-1447">
              <Icon className="w-4 h-4" data-id="element-1448" />
              <span data-id="element-1449">{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && <span className={`ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${isActive ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-muted text-muted-foreground'}`} data-id="element-1450">
                  {tab.count}
                </span>}
            </button>;
      })}
      </div>;
  };
  const renderOverviewTab = () => <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-300" data-id="element-1451">
      {/* Left Column (2/3) */}
      <div className="lg:col-span-2 space-y-6" data-id="element-1452">
        <Card data-id="element-1453">
          <CardHeader className="pb-3" data-id="element-1454">
            <CardTitle className="text-lg" data-id="element-1455">Matter Summary</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1456">
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1457">
              <div data-id="element-1458">
                <p className="text-xs text-muted-foreground mb-1" data-id="element-1459">
                  Case Number
                </p>
                <p className="text-sm font-medium" data-id="element-1460">
                  {WORKSPACE_MATTER.caseNumber}
                </p>
              </div>
              <div data-id="element-1461">
                <p className="text-xs text-muted-foreground mb-1" data-id="element-1462">
                  Jurisdiction
                </p>
                <p className="text-sm font-medium" data-id="element-1463">
                  {WORKSPACE_MATTER.jurisdiction}
                </p>
              </div>
              <div data-id="element-1464">
                <p className="text-xs text-muted-foreground mb-1" data-id="element-1465">
                  Referral Source
                </p>
                <p className="text-sm font-medium" data-id="element-1466">
                  {WORKSPACE_MATTER.referralSource}
                </p>
              </div>
              <div data-id="element-1467">
                <p className="text-xs text-muted-foreground mb-1" data-id="element-1468">Filed Date</p>
                <p className="text-sm font-medium" data-id="element-1469">
                  {WORKSPACE_MATTER.filedDate}
                </p>
              </div>
            </div>
            <div data-id="element-1470">
              <p className="text-xs text-muted-foreground mb-2" data-id="element-1471">
                Case Theory / Summary
              </p>
              <div className="p-3 bg-muted/50 rounded-md text-sm border border-border/50" data-id="element-1472">
                {WORKSPACE_MATTER.caseTheory}
              </div>
            </div>
            <div className="mt-4" data-id="element-1473">
              <p className="text-xs text-muted-foreground mb-2" data-id="element-1474">Matter Tags</p>
              <div className="flex flex-wrap gap-2" data-id="element-1475">
                {MATTER_TAGS.map(tag => <Badge key={tag} variant="secondary" className="text-xs font-normal" data-id="element-1476">
                    <Tag className="w-3 h-3 mr-1 text-muted-foreground" data-id="element-1477" />
                    {tag}
                  </Badge>)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1478">
          <CardHeader className="pb-3 flex flex-row items-center justify-between" data-id="element-1479">
            <CardTitle className="text-lg flex items-center" data-id="element-1480">
              <Users className="w-5 h-5 mr-2 text-muted-foreground" data-id="element-1481" />
              Key Parties
            </CardTitle>
            <Button variant="ghost" size="sm" data-id="element-1482">
              Manage
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1483">
            <table className="w-full text-sm text-left" data-id="element-1484">
              <thead className="text-xs text-muted-foreground bg-muted/50 border-y" data-id="element-1485">
                <tr data-id="element-1486">
                  <th className="px-4 py-2 font-medium" data-id="element-1487">Role</th>
                  <th className="px-4 py-2 font-medium" data-id="element-1488">Name</th>
                  <th className="px-4 py-2 font-medium" data-id="element-1489">Counsel / Details</th>
                </tr>
              </thead>
              <tbody className="divide-y" data-id="element-1490">
                {WORKSPACE_PARTIES.map((party, i) => <tr key={i} className="hover:bg-muted/30" data-id="element-1491">
                    <td className="px-4 py-3 text-muted-foreground" data-id="element-1492">
                      {party.role}
                    </td>
                    <td className="px-4 py-3 font-medium" data-id="element-1493">{party.name}</td>
                    <td className="px-4 py-3 text-muted-foreground" data-id="element-1494">
                      {party.counsel}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card data-id="element-1495">
          <CardHeader className="pb-3 flex flex-row items-center justify-between" data-id="element-1496">
            <CardTitle className="text-lg flex items-center" data-id="element-1497">
              <Activity className="w-5 h-5 mr-2 text-muted-foreground" data-id="element-1498" />
              Recent Activity
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('audit')} data-id="element-1499">
              View All
            </Button>
          </CardHeader>
          <CardContent data-id="element-1500">
            <div className="space-y-4" data-id="element-1501">
              {WORKSPACE_ACTIVITY.slice(0, 5).map(act => <div key={act.id} className="flex items-start space-x-3" data-id="element-1502">
                  <div className={`mt-0.5 p-1.5 rounded-full ${act.type === 'ai' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400' : act.type === 'system' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' : 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400'}`} data-id="element-1503">
                    {act.type === 'ai' ? <BrainCircuit className="w-3.5 h-3.5" data-id="element-1504" /> : act.type === 'system' ? <Settings2 className="w-3.5 h-3.5" data-id="element-1505" /> : <Users className="w-3.5 h-3.5" data-id="element-1506" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1507">
                    <p className="text-sm" data-id="element-1508">
                      <span className="font-medium text-foreground" data-id="element-1509">
                        {act.user}
                      </span>{' '}
                      <span className="text-muted-foreground" data-id="element-1510">
                        {act.action}
                      </span>{' '}
                      <span className="font-medium text-foreground" data-id="element-1511">
                        {act.detail}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-id="element-1512">
                      {act.timestamp}
                    </p>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Column (1/3) */}
      <div className="space-y-6" data-id="element-1513">
        <Card data-id="element-1514">
          <CardHeader className="pb-3" data-id="element-1515">
            <CardTitle className="text-lg flex items-center" data-id="element-1516">
              <Calendar className="w-5 h-5 mr-2 text-muted-foreground" data-id="element-1517" />
              Key Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1518">
            <div className="divide-y" data-id="element-1519">
              {WORKSPACE_KEY_DATES.map((kd, i) => <div key={i} className="px-4 py-3 flex justify-between items-center hover:bg-muted/30" data-id="element-1520">
                  <div className="flex items-center space-x-3" data-id="element-1521">
                    <div className={`w-2 h-2 rounded-full ${kd.status === 'past' ? 'bg-muted-foreground/50' : 'bg-blue-500'}`} data-id="element-1522" />

                    <span className={`text-sm ${kd.status === 'past' ? 'text-muted-foreground line-through decoration-muted-foreground/30' : 'font-medium'}`} data-id="element-1523">
                      {kd.event}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap ml-4" data-id="element-1524">
                    {kd.date}
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1525">
          <CardHeader className="pb-3" data-id="element-1526">
            <CardTitle className="text-lg flex items-center" data-id="element-1527">
              <Database className="w-5 h-5 mr-2 text-muted-foreground" data-id="element-1528" />
              Corpus Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-1529">
            <div data-id="element-1530">
              <div className="flex justify-between items-end mb-2" data-id="element-1531">
                <span className="text-sm font-medium" data-id="element-1532">Document Categories</span>
                <span className="text-xs text-muted-foreground" data-id="element-1533">
                  {WORKSPACE_DOCUMENTS.length} total
                </span>
              </div>
              <div className="space-y-2" data-id="element-1534">
                {Object.entries(DOC_CATEGORY_COUNTS).map(([cat, count]) => <div key={cat} className="flex items-center text-sm" data-id="element-1535">
                    <span className="w-32 text-muted-foreground truncate" data-id="element-1536">
                      {cat}
                    </span>
                    <div className="flex-1 mx-3 h-2 bg-muted rounded-full overflow-hidden" data-id="element-1537">
                      <div className="h-full bg-blue-500 rounded-full" style={{
                    width: `${count / WORKSPACE_DOCUMENTS.length * 100}%`
                  }} data-id="element-1538" />
                    </div>
                    <span className="w-6 text-right font-medium" data-id="element-1539">{count}</span>
                  </div>)}
              </div>
            </div>

            <div data-id="element-1540">
              <p className="text-sm font-medium mb-2" data-id="element-1541">Source Locations</p>
              <div className="grid grid-cols-2 gap-2" data-id="element-1542">
                {Object.entries(SOURCE_COUNTS).map(([source, count]) => <div key={source} className="bg-muted/50 p-2 rounded border flex justify-between items-center" data-id="element-1543">
                    <span className="text-xs text-muted-foreground" data-id="element-1544">
                      {source}
                    </span>
                    <Badge variant="secondary" className="text-[10px]" data-id="element-1545">
                      {count}
                    </Badge>
                  </div>)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 dark:border-blue-900/50 bg-blue-50/30 dark:bg-blue-950/10" data-id="element-1546">
          <CardHeader className="pb-3" data-id="element-1547">
            <CardTitle className="text-lg flex items-center text-blue-800 dark:text-blue-400" data-id="element-1548">
              <Shield className="w-5 h-5 mr-2" data-id="element-1549" />
              Policy & Isolation Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-1550">
            <div className="space-y-3" data-id="element-1551">
              <div data-id="element-1552">
                <div className="flex justify-between items-center mb-1" data-id="element-1553">
                  <span className="text-sm font-medium text-foreground" data-id="element-1554">
                    Matter Isolation
                  </span>
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-1555">
                    Enforced
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-1556">
                  All queries and retrieval scoped to this matter only.
                </p>
              </div>

              <div data-id="element-1557">
                <div className="flex justify-between items-center mb-1" data-id="element-1558">
                  <span className="text-sm font-medium text-foreground" data-id="element-1559">
                    Citation Policy
                  </span>
                  <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200" data-id="element-1560">
                    Strict
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-1561">
                  All AI outputs require source citations.
                </p>
              </div>

              <div data-id="element-1562">
                <div className="flex justify-between items-center mb-1" data-id="element-1563">
                  <span className="text-sm font-medium text-foreground" data-id="element-1564">
                    Review Policy
                  </span>
                  <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200" data-id="element-1565">
                    Full Review
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-1566">
                  Attorney approval required for all AI outputs.
                </p>
              </div>

              <div data-id="element-1567">
                <div className="flex justify-between items-center mb-1" data-id="element-1568">
                  <span className="text-sm font-medium text-foreground" data-id="element-1569">
                    Confidence Threshold
                  </span>
                  <span className="text-xs font-medium text-blue-700" data-id="element-1570">
                    75% min
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 mb-1" data-id="element-1571">
                  <div className="bg-blue-500 h-1.5 rounded-full" style={{
                  width: '75%'
                }} data-id="element-1572"></div>
                </div>
              </div>

              <div className="pt-2 border-t border-blue-100 dark:border-blue-900/50" data-id="element-1573">
                <div className="flex justify-between items-center mb-1" data-id="element-1574">
                  <span className="text-sm font-medium text-foreground" data-id="element-1575">
                    Cross-Matter Access
                  </span>
                  <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200" data-id="element-1576">
                    Blocked
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground" data-id="element-1577">
                  No cross-matter data leakage permitted.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 dark:border-orange-900/50 bg-orange-50/30 dark:bg-orange-950/10" data-id="element-1578">
          <CardHeader className="pb-3" data-id="element-1579">
            <CardTitle className="text-lg flex items-center text-orange-800 dark:text-orange-400" data-id="element-1580">
              <AlertTriangle className="w-5 h-5 mr-2" data-id="element-1581" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-1582">
            <div className="space-y-3" data-id="element-1583">
              {WORKSPACE_REVIEW_ITEMS.filter(r => r.status === 'Pending').slice(0, 3).map(item => <div key={item.id} className="flex flex-col space-y-1" data-id="element-1584">
                    <div className="flex justify-between items-start" data-id="element-1585">
                      <span className="text-sm font-medium" data-id="element-1586">{item.title}</span>
                      <Badge variant="outline" className="text-[10px] bg-background" data-id="element-1587">
                        {item.type}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground truncate" data-id="element-1588">
                      {item.sourceDocTitle}
                    </span>
                  </div>)}
              <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => setActiveTab('review')} data-id="element-1589">
                Go to Review Queue
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
  const renderDocumentsTab = () => <div className="space-y-4 animate-in fade-in duration-300" data-id="element-1590">
      <div className="flex flex-col sm:flex-row gap-4 justify-between" data-id="element-1591">
        <div className="flex-1 flex space-x-2" data-id="element-1592">
          <div className="relative flex-1 max-w-md" data-id="element-1593">
            <Input placeholder="Search documents, text, or Bates numbers..." icon={<Search className="w-4 h-4" data-id="element-1595" />} data-id="element-1594" />
          </div>
          <Button variant="outline" data-id="element-1596">
            <Filter className="w-4 h-4 mr-2" data-id="element-1597" />
            Filters
          </Button>
        </div>
        <div className="flex space-x-2" data-id="element-1598">
          <Button variant="outline" data-id="element-1599">
            <Download className="w-4 h-4 mr-2" data-id="element-1600" />
            Export Index
          </Button>
          <Button data-id="element-1601">
            <Upload className="w-4 h-4 mr-2" data-id="element-1602" />
            Upload
          </Button>
        </div>
      </div>

      <Card data-id="element-1603">
        <div className="overflow-x-auto" data-id="element-1604">
          <table className="w-full text-sm text-left" data-id="element-1605">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b" data-id="element-1606">
              <tr data-id="element-1607">
                <th className="px-4 py-3 font-medium w-8" data-id="element-1608"></th>
                <th className="px-4 py-3 font-medium" data-id="element-1609">Title & Bates</th>
                <th className="px-4 py-3 font-medium" data-id="element-1610">Type</th>
                <th className="px-4 py-3 font-medium" data-id="element-1611">Date Rcvd</th>
                <th className="px-4 py-3 font-medium" data-id="element-1612">AI Conf.</th>
                <th className="px-4 py-3 font-medium" data-id="element-1613">Status</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-1614">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" data-id="element-1615">
              {WORKSPACE_DOCUMENTS.map(doc => <tr key={doc.id} className="hover:bg-muted/30 group" data-id="element-1616">
                  <td className="px-4 py-3" data-id="element-1617">
                    {doc.isKey ? <Tag className="w-4 h-4 text-amber-500" data-id="element-1618" /> : <FileText className="w-4 h-4 text-muted-foreground" data-id="element-1619" />}
                  </td>
                  <td className="px-4 py-3" data-id="element-1620">
                    <div className="font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer truncate max-w-[300px]" data-id="element-1621">
                      {doc.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1622">
                      {doc.batesStart !== 'N/A' ? `${doc.batesStart} - ${doc.batesEnd}` : 'No Bates'}{' '}
                      • {doc.pages} pgs
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground" data-id="element-1623">
                    {doc.type}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap" data-id="element-1624">
                    {doc.dateReceived}
                  </td>
                  <td className="px-4 py-3" data-id="element-1625">
                    <div className="flex items-center space-x-2" data-id="element-1626">
                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-1627">
                        <div className={`h-full rounded-full ${doc.confidence > 90 ? 'bg-emerald-500' : doc.confidence > 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${doc.confidence}%`
                    }} data-id="element-1628" />
                      </div>
                      <span className="text-xs text-muted-foreground" data-id="element-1629">
                        {doc.confidence}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-1630">
                    <Badge variant={doc.reviewStatus === 'Reviewed' ? 'success' : doc.reviewStatus === 'Flagged' ? 'destructive' : doc.reviewStatus === 'Excluded' ? 'secondary' : 'warning'} className="text-[10px]" data-id="element-1631">
                      {doc.reviewStatus}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1632">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1633">
                      <MoreVertical className="w-4 h-4" data-id="element-1634" />
                    </Button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
  const renderSearchTab = () => <div className="space-y-6 max-w-5xl mx-auto animate-in fade-in duration-300" data-id="element-1635">
      <form onSubmit={handleSearch} className="relative" data-id="element-1636">
        <div className="relative flex items-center" data-id="element-1637">
          <Search className="absolute left-4 w-5 h-5 text-muted-foreground" data-id="element-1638" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-14 pl-12 pr-32 rounded-xl border-2 border-input bg-background text-lg shadow-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" placeholder="Ask a question about this matter..." data-id="element-1639" />

          <div className="absolute right-2 flex space-x-2" data-id="element-1640">
            <Button type="submit" className="h-10 px-6" disabled={isSearching || !searchQuery.trim()} data-id="element-1641">
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>
      </form>

      {!showResults && !isSearching && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1642">
          <div data-id="element-1643">
            <h3 className="text-sm font-medium text-muted-foreground mb-3" data-id="element-1644">
              Suggested Queries
            </h3>
            <div className="space-y-2" data-id="element-1645">
              {["What is Dr. Smith's opinion on causation?", 'Are there any mentions of prior back injuries?', 'Summarize the ER visit on Oct 12', 'What hours did the driver log on the day of the incident?'].map((q, i) => <button key={i} onClick={() => {
            setSearchQuery(q);
            setTimeout(() => triggerSearch(), 0);
          }} className="w-full text-left p-3 text-sm bg-muted/30 hover:bg-muted/60 border rounded-lg transition-colors flex items-center justify-between group" data-id="element-1646">
                  <span data-id="element-1647">{q}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1648" />
                </button>)}
            </div>
          </div>
          <div data-id="element-1649">
            <h3 className="text-sm font-medium text-muted-foreground mb-3" data-id="element-1650">
              Recent Searches
            </h3>
            <div className="space-y-2" data-id="element-1651">
              {WORKSPACE_SEARCH_HISTORY.map(sh => <div key={sh.id} className="flex items-center justify-between p-3 text-sm bg-background border rounded-lg" data-id="element-1652">
                  <div className="flex items-center space-x-2 truncate" data-id="element-1653">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" data-id="element-1654" />
                    <span className="truncate" data-id="element-1655">{sh.query}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-4" data-id="element-1656">
                    {sh.resultCount} results
                  </span>
                </div>)}
            </div>
          </div>
        </div>}

      {isSearching && <div className="py-16 flex flex-col items-center justify-center space-y-4 text-muted-foreground" data-id="element-1657">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-1658"></div>
          <p className="text-sm" data-id="element-1659">
            Scanning matter corpus and synthesizing answer...
          </p>
        </div>}

      {showResults && !isSearching && <div className="space-y-6" data-id="element-1660">
          <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-950/10" data-id="element-1661">
            <CardContent className="p-6" data-id="element-1662">
              <div className="flex items-center space-x-2 mb-4" data-id="element-1663">
                <BrainCircuit className="w-5 h-5 text-blue-600" data-id="element-1664" />
                <h3 className="font-semibold text-lg" data-id="element-1665">Synthesized Answer</h3>
                <Badge variant="outline" className="ml-auto bg-background text-xs" data-id="element-1666">
                  High Confidence
                </Badge>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none text-foreground leading-relaxed" data-id="element-1667">
                <p data-id="element-1668">
                  Based on the documents in this matter, there is evidence
                  regarding the driver's hours of service. The Defendant Driver
                  ELD Logs for October 2023{' '}
                  <sup className="text-blue-600 cursor-pointer hover:underline font-bold" data-id="element-1669">
                    [1]
                  </sup>{' '}
                  indicate that James Whitfield had been driving for 13.5 hours
                  prior to the collision, exceeding the FMCSA 11-hour driving
                  limit.
                </p>
                <p data-id="element-1670">
                  Additionally, the Police Accident Report{' '}
                  <sup className="text-blue-600 cursor-pointer hover:underline font-bold" data-id="element-1671">
                    [2]
                  </sup>{' '}
                  notes that the driver stated he was "exhausted" at the scene.
                  However, the Lone Star Freight Safety Manual{' '}
                  <sup className="text-blue-600 cursor-pointer hover:underline font-bold" data-id="element-1672">
                    [3]
                  </sup>{' '}
                  outlines strict fatigue management protocols, suggesting a
                  potential violation of company policy as well as federal
                  regulations.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-blue-100 dark:border-blue-900/50 flex items-center justify-between text-xs text-muted-foreground" data-id="element-1673">
                <span data-id="element-1674">AI-assisted results require attorney review.</span>
                <div className="flex space-x-2" data-id="element-1675">
                  <Button variant="ghost" size="sm" className="h-6 px-2" data-id="element-1676">
                    Copy
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2" data-id="element-1677">
                    Save to Notes
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div data-id="element-1678">
            <h3 className="font-semibold text-lg px-1 mb-4" data-id="element-1679">
              Source Documents (3)
            </h3>
            <div className="space-y-3" data-id="element-1680">
              {[WORKSPACE_DOCUMENTS[6], WORKSPACE_DOCUMENTS[5], WORKSPACE_DOCUMENTS[7]].map((doc, index) => <Card key={doc.id} className="hover:border-blue-300 transition-colors cursor-pointer group" data-id="element-1681">
                  <CardContent className="p-4 flex items-start space-x-4" data-id="element-1682">
                    <div className="flex flex-col items-center space-y-1 mt-1" data-id="element-1683">
                      <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold" data-id="element-1684">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1 space-y-2" data-id="element-1685">
                      <div className="flex items-start justify-between" data-id="element-1686">
                        <div data-id="element-1687">
                          <h4 className="font-medium text-base group-hover:text-blue-600 transition-colors" data-id="element-1688">
                            {doc.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-0.5" data-id="element-1689">
                            Bates: {doc.batesStart} - {doc.batesEnd} •{' '}
                            {doc.type}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-[10px]" data-id="element-1690">
                          {doc.confidence}% Match
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md border border-border/50 italic" data-id="element-1691">
                        "...driver log indicates{' '}
                        <span className="bg-yellow-200 dark:bg-yellow-900/50 text-foreground px-1 rounded" data-id="element-1692">
                          13.5 hours of continuous driving
                        </span>{' '}
                        prior to the incident recorded at 18:45..."
                      </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>}
    </div>;
  const renderChronologyTab = () => <div className="space-y-4 animate-in fade-in duration-300 h-full flex flex-col" data-id="element-1693">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-1694">
        <div className="flex flex-wrap gap-2" data-id="element-1695">
          <Input placeholder="Filter events..." className="w-64" icon={<Filter className="w-4 h-4" data-id="element-1697" />} data-id="element-1696" />
          <Button variant="outline" data-id="element-1698">Provider</Button>
          <Button variant="outline" data-id="element-1699">Significance</Button>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1700">
          <Button variant="outline" data-id="element-1701">
            <Download className="w-4 h-4 mr-2" data-id="element-1702" />
            Export
          </Button>
          <Button data-id="element-1703">
            <RefreshCw className="w-4 h-4 mr-2" data-id="element-1704" />
            Update from Corpus
          </Button>
        </div>
      </div>

      <Card className="flex-1 overflow-hidden flex flex-col" data-id="element-1705">
        <div className="overflow-y-auto flex-1" data-id="element-1706">
          <table className="w-full text-sm text-left" data-id="element-1707">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/50 sticky top-0 shadow-sm z-10" data-id="element-1708">
              <tr data-id="element-1709">
                <th className="px-4 py-3 font-medium w-28" data-id="element-1710">Date</th>
                <th className="px-4 py-3 font-medium w-40" data-id="element-1711">
                  Provider / Facility
                </th>
                <th className="px-4 py-3 font-medium w-32" data-id="element-1712">Event Type</th>
                <th className="px-4 py-3 font-medium" data-id="element-1713">Description & Notes</th>
                <th className="px-4 py-3 font-medium w-32" data-id="element-1714">Source</th>
                <th className="px-4 py-3 font-medium w-16 text-center" data-id="element-1715">Sig.</th>
              </tr>
            </thead>
            <tbody className="divide-y" data-id="element-1716">
              {WORKSPACE_CHRONOLOGY.map(entry => <tr key={entry.id} className={`hover:bg-muted/30 ${entry.hasAmbiguity ? 'bg-orange-50/30 dark:bg-orange-950/10' : ''}`} data-id="element-1717">
                  <td className="px-4 py-4 font-medium whitespace-nowrap align-top" data-id="element-1718">
                    {entry.date}
                  </td>
                  <td className="px-4 py-4 align-top" data-id="element-1719">
                    <div className="font-medium" data-id="element-1720">{entry.provider}</div>
                    <div className="text-xs text-muted-foreground" data-id="element-1721">
                      {entry.facility}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top" data-id="element-1722">
                    <Badge variant="outline" className="text-[10px] bg-background" data-id="element-1723">
                      {entry.eventType}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 align-top" data-id="element-1724">
                    <p className="text-foreground" data-id="element-1725">{entry.description}</p>
                    {entry.hasAmbiguity && <div className="mt-2 p-2 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded text-xs text-orange-800 dark:text-orange-300 flex items-start" data-id="element-1726">
                        <AlertTriangle className="w-3.5 h-3.5 mr-1.5 shrink-0 mt-0.5" data-id="element-1727" />
                        <span data-id="element-1728">{entry.ambiguityNote}</span>
                      </div>}
                  </td>
                  <td className="px-4 py-4 align-top" data-id="element-1729">
                    <div className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium text-xs" data-id="element-1730">
                      {entry.batesRef}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top text-center" data-id="element-1731">
                    <div className={`w-3 h-3 rounded-full mx-auto ${entry.significance === 'High' ? 'bg-red-500' : entry.significance === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} title={`${entry.significance} Significance`} data-id="element-1732" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
  const renderWorkflowsTab = () => <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1733">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1734">
        {WORKSPACE_WORKFLOWS.map(wf => <Card key={wf.id} className="flex flex-col" data-id="element-1735">
            <CardHeader className="pb-3" data-id="element-1736">
              <div className="flex justify-between items-start mb-2" data-id="element-1737">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg" data-id="element-1738">
                  <GitBranch className="w-5 h-5 text-slate-600 dark:text-slate-300" data-id="element-1739" />
                </div>
                <Badge variant={wf.status === 'Completed' ? 'success' : wf.status === 'Running' ? 'default' : wf.status === 'Failed' ? 'destructive' : 'secondary'} data-id="element-1740">
                  {wf.status}
                </Badge>
              </div>
              <CardTitle className="text-lg" data-id="element-1741">{wf.name}</CardTitle>
            </CardHeader>
            <CardContent className="flex-1" data-id="element-1742">
              <div className="space-y-3 text-sm" data-id="element-1743">
                <div className="flex justify-between text-muted-foreground" data-id="element-1744">
                  <span data-id="element-1745">Last Run</span>
                  <span className="text-foreground" data-id="element-1746">{wf.lastRun}</span>
                </div>
                <div className="flex justify-between text-muted-foreground" data-id="element-1747">
                  <span data-id="element-1748">Duration</span>
                  <span className="text-foreground" data-id="element-1749">{wf.duration}</span>
                </div>

                {wf.status === 'Running' && <div className="mt-4 space-y-1" data-id="element-1750">
                    <div className="flex justify-between text-xs mb-1" data-id="element-1751">
                      <span data-id="element-1752">Progress</span>
                      <span data-id="element-1753">
                        {wf.stepsCompleted} / {wf.totalSteps} steps
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5" data-id="element-1754">
                      <div className="bg-blue-600 h-1.5 rounded-full animate-pulse" style={{
                  width: `${wf.stepsCompleted / wf.totalSteps * 100}%`
                }} data-id="element-1755"></div>
                    </div>
                  </div>}
              </div>
            </CardContent>
            <CardFooter className="pt-4 border-t bg-muted/20 flex justify-between" data-id="element-1756">
              <Button variant="ghost" size="sm" className="text-muted-foreground" data-id="element-1757">
                View Logs
              </Button>
              <Button size="sm" disabled={wf.status === 'Running'} data-id="element-1758">
                <Play className="w-3.5 h-3.5 mr-1.5" data-id="element-1759" /> Run Now
              </Button>
            </CardFooter>
          </Card>)}
      </div>
    </div>;
  const renderReviewTab = () => <div className="space-y-4 animate-in fade-in duration-300" data-id="element-1760">
      <div className="flex space-x-2 mb-6" data-id="element-1761">
        <Badge variant="secondary" className="px-3 py-1 text-sm cursor-pointer hover:bg-muted" data-id="element-1762">
          All (6)
        </Badge>
        <Badge variant="default" className="px-3 py-1 text-sm cursor-pointer" data-id="element-1763">
          Pending (4)
        </Badge>
        <Badge variant="outline" className="px-3 py-1 text-sm cursor-pointer text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20" data-id="element-1764">
          Approved (1)
        </Badge>
        <Badge variant="outline" className="px-3 py-1 text-sm cursor-pointer text-red-600 border-red-200 bg-red-50 dark:bg-red-950/20" data-id="element-1765">
          Rejected (1)
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-4" data-id="element-1766">
        {WORKSPACE_REVIEW_ITEMS.filter(r => r.status === 'Pending').map(item => <Card key={item.id} className="border-l-4 border-l-amber-500" data-id="element-1767">
              <CardContent className="p-5" data-id="element-1768">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-1769">
                  <div className="flex-1" data-id="element-1770">
                    <div className="flex items-center space-x-2 mb-1" data-id="element-1771">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider" data-id="element-1772">
                        {item.type}
                      </Badge>
                      <span className="text-xs text-muted-foreground" data-id="element-1773">
                        {item.createdAt}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2" data-id="element-1774">{item.title}</h3>
                    <p className="text-sm text-foreground mb-3" data-id="element-1775">
                      {item.description}
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground bg-muted/50 p-2 rounded inline-flex" data-id="element-1776">
                      <FileSearch className="w-3.5 h-3.5 mr-1.5" data-id="element-1777" />
                      Source:{' '}
                      <span className="font-medium text-blue-600 dark:text-blue-400 ml-1 cursor-pointer hover:underline" data-id="element-1778">
                        {item.sourceDocTitle}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-4 min-w-[200px]" data-id="element-1779">
                    <div className="w-full" data-id="element-1780">
                      <div className="flex justify-between text-xs mb-1" data-id="element-1781">
                        <span className="text-muted-foreground" data-id="element-1782">
                          AI Confidence
                        </span>
                        <span className="font-medium" data-id="element-1783">{item.confidence}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-1.5" data-id="element-1784">
                        <div className={`h-1.5 rounded-full ${item.confidence > 90 ? 'bg-emerald-500' : item.confidence > 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${item.confidence}%`
                  }} data-id="element-1785"></div>
                      </div>
                    </div>

                    <div className="flex space-x-2 w-full" data-id="element-1786">
                      <Button variant="outline" className="flex-1 text-destructive hover:text-destructive hover:bg-destructive/10" data-id="element-1787">
                        <XCircle className="w-4 h-4 mr-1.5" data-id="element-1788" /> Reject
                      </Button>
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-1789">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" data-id="element-1790" /> Approve
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>)}
      </div>
    </div>;
  const renderAuditTab = () => <div className="space-y-4 animate-in fade-in duration-300" data-id="element-1791">
      <div className="flex justify-between items-center mb-4" data-id="element-1792">
        <div className="relative w-72" data-id="element-1793">
          <Input placeholder="Filter audit log..." icon={<Filter className="w-4 h-4" data-id="element-1795" />} data-id="element-1794" />
        </div>
        <Button variant="outline" data-id="element-1796">
          <Download className="w-4 h-4 mr-2" data-id="element-1797" />
          Export Log
        </Button>
      </div>

      <Card data-id="element-1798">
        <div className="overflow-x-auto" data-id="element-1799">
          <table className="w-full text-sm text-left" data-id="element-1800">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b" data-id="element-1801">
              <tr data-id="element-1802">
                <th className="px-4 py-3 font-medium" data-id="element-1803">Timestamp</th>
                <th className="px-4 py-3 font-medium" data-id="element-1804">User / System</th>
                <th className="px-4 py-3 font-medium" data-id="element-1805">Action</th>
                <th className="px-4 py-3 font-medium" data-id="element-1806">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y" data-id="element-1807">
              {WORKSPACE_AUDIT_LOG.map(log => <tr key={log.id} className="hover:bg-muted/30" data-id="element-1808">
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap" data-id="element-1809">
                    {log.timestamp}
                  </td>
                  <td className="px-4 py-3" data-id="element-1810">
                    <span className={`font-medium ${log.user === 'System' ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'}`} data-id="element-1811">
                      {log.user}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-1812">
                    <Badge variant="outline" className="bg-background font-normal" data-id="element-1813">
                      {log.action}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground" data-id="element-1814">
                    {log.detail}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
  return <div className="space-y-6 h-full flex flex-col" data-id="element-1815">
      {/* Matter Header */}
      <div className="shrink-0" data-id="element-1816">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-3" data-id="element-1817">
          <button onClick={() => onNavigate('matters')} className="hover:text-foreground flex items-center transition-colors" data-id="element-1818">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" data-id="element-1819" /> Back to Matters
          </button>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-1820">
          <div data-id="element-1821">
            <div className="flex items-center space-x-3 mb-1" data-id="element-1822">
              <h1 className="text-2xl font-bold tracking-tight text-foreground" data-id="element-1823">
                {WORKSPACE_MATTER.name}
              </h1>
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300 border-transparent" data-id="element-1824">
                {WORKSPACE_MATTER.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground" data-id="element-1825">
              {WORKSPACE_MATTER.type} • Lead:{' '}
              {WORKSPACE_MATTER.responsibleAttorney} • Opened:{' '}
              {WORKSPACE_MATTER.filedDate}
            </p>
          </div>
          <div className="flex space-x-2 shrink-0" data-id="element-1826">
            <Button variant="outline" data-id="element-1827">
              <Upload className="w-4 h-4 mr-2" data-id="element-1828" />
              Upload Docs
            </Button>
            <Button onClick={() => onNavigate('copilot')} data-id="element-1829">
              <BrainCircuit className="w-4 h-4 mr-2" data-id="element-1830" />
              Ask Copilot
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="shrink-0" data-id="element-1831">{renderTabNavigation()}</div>

      {/* Tab Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-8" data-id="element-1832">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'search' && renderSearchTab()}
        {activeTab === 'chronology' && renderChronologyTab()}
        {activeTab === 'workflows' && renderWorkflowsTab()}
        {activeTab === 'drafting' && <div className="flex flex-col items-center justify-center h-64 text-center space-y-4" data-id="element-1833">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-full" data-id="element-1834">
              <PenTool className="w-8 h-8 text-blue-600 dark:text-blue-400" data-id="element-1835" />
            </div>
            <div data-id="element-1836">
              <h3 className="text-lg font-medium" data-id="element-1837">Drafting Copilot</h3>
              <p className="text-muted-foreground max-w-md mx-auto mt-1" data-id="element-1838">
                Access the dedicated drafting workspace to generate pleadings,
                discovery, and correspondence grounded in this matter's corpus.
              </p>
            </div>
            <Button onClick={() => onNavigate('copilot')} data-id="element-1839">
              Open Drafting Workspace
            </Button>
          </div>}
        {activeTab === 'review' && renderReviewTab()}
        {activeTab === 'audit' && renderAuditTab()}
      </div>
    </div>;
}